/* Thin JSON client over fetch. Same-origin cookies carry the session. */

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        message: string,
        public readonly details: string[] = [],
        /* The server's machine-readable reason, which this class used to
         * throw away. The app's own ApiError has carried it since 7.1 and
         * this one did not, so every caller here had to match on a sentence.
         *
         * It was added for password.mustChange, which has since been removed
         * (drizzle/0050), and it stays because matching a sentence was always
         * the wrong way round: portal.notAManager and password.wrongCurrent
         * both read it now, and a copy edit to a server message should not be
         * able to change what a screen does. */
        public readonly code?: string,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

/**
 * What to put on the screen when a call failed.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT JUST `err.message`.
 *
 * The server answers a failed validation with `{ error: "Invalid request",
 * details: ["expected: String must contain at least 3 character(s)"] }`.
 * ApiError has carried `details` all along and most screens showed only the
 * message, so a courier pressing File it on 1 November -- go-live morning --
 * got the words "Invalid request" and nothing else. The server had already
 * said exactly which box was wrong; the screen dropped it on the floor.
 *
 * Two characters of a field name are worth more than any sentence we could
 * write here, because they say WHICH control to go and fix.
 */
export function why(err: unknown, fallback: string): string {
    if (!(err instanceof ApiError)) return fallback;
    return err.details.length > 0 ? `${err.message} (${err.details.join('; ')})` : err.message;
}

/* ───────────────────────────── a session that ended is not an error ──────
 *
 * A pharmacist left the portal open over lunch. The session idled out after
 * thirty minutes, the next poll came back 401, and the page rendered it the
 * way it renders any failure: a red "Not authenticated" over a screen still
 * showing "Live · updated just now" and four zeros.
 *
 * The banner was the small half. The zeros were the dangerous half -- SENT
 * TO US 0, DELIVERED 0 -- because a counter reading that does not think
 * "my session expired", they think their deliveries have gone.
 *
 * So one place decides: a 401 from anything except signing in means the
 * session is over, and the app says so by showing the sign-in page, which is
 * where that person was heading anyway. Registered by the auth provider
 * rather than imported from it, because this file knows about fetch and
 * should not know about React.
 */
let onSessionEnded: (() => void) | null = null;

export function whenSessionEnds(fn: () => void): void {
    onSessionEnded = fn;
}

/* WHICH 401 THIS IS, FROM THE SERVER RATHER THAN FROM THE URL.
 *
 * Two different facts arrive as 401. "Your session is over" is one.
 * "The password you just retyped is wrong" is the other, and POST
 * /api/devices/enrol answers it that way to somebody who is perfectly well
 * signed in and setting up a PIN -- an existing test caught this, after a
 * first version of this file keyed off the URL and signed them out.
 *
 * A URL list would have rotted the first time an endpoint checked a
 * password. The server knows which fact it means, so it tags the first one
 * and this reads the tag. An untagged 401 is left to the page, which is the
 * safe direction: the worst case is the old banner, not an unwanted
 * sign-out. */
const SESSION_ENDED = 'auth.sessionEnded';

export async function api<T = unknown>(url: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
    const { json, ...rest } = init;
    const res = await fetch(url, {
        credentials: 'same-origin',
        ...rest,
        headers: { Accept: 'application/json', ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}), ...(rest.headers ?? {}) },
        body: json !== undefined ? JSON.stringify(json) : rest.body,
    });
    const text = await res.text();
    let body: unknown = null;
    try { body = text ? JSON.parse(text) : null; } catch { body = null; }
    if (!res.ok) {
        const b = (body ?? {}) as { error?: string; details?: string[]; code?: string };
        /* Before the throw, so the shell is already on its way to the
           sign-in page by the time a caller's catch runs. Whatever message
           that catch sets lands on a component that is unmounting. */
        if (res.status === 401 && b.code === SESSION_ENDED) onSessionEnded?.();
        throw new ApiError(res.status, b.error || `Request failed (${res.status})`, b.details ?? [], b.code);
    }
    return body as T;
}

export interface SessionUser {
    id: number;
    username: string;
    name: string;
    role: 'admin' | 'staff' | 'driver';
    route: string | null;
}

export interface ProjectMembership {
    id: number;
    code: string;
    name: string;
    timezone: string;
    /* FOUR ROLES, NOT THREE. `lead` -- the University Health site manager --
     * was missing here while the server has had it in PROJECT_ROLES all
     * along, and a type that cannot express a role the server sends does
     * not merely fail to describe it: it stops anybody writing code for it.
     *
     * pages/uh/Drivers.tsx is what that cost. Its guard reads
     * `role !== 'admin'` because `role === 'lead'` was a type error, so a
     * site manager following the Drivers record link from their own project
     * page landed on "Not available" -- a link offered to them that went
     * nowhere -- while modules/uh/driver-records.ts had been admitting
     * leads, with the money stripped, since ticket 7.20.
     *
     * Found on 29 October by signing in as the site manager and clicking
     * every link on the page. */
    role: 'admin' | 'lead' | 'courier' | 'pharmacy';
    /** The counter this person is based at, from the membership's
     *  settings.homeSiteId.
     *
     *  NULL IS A REAL ANSWER and means a floater, not a missing record:
     *  some drivers cover wherever the day needs them.
     *
     *  OPTIONAL IS ALSO REAL, and the type says so deliberately. A phone
     *  holds its bundle across a deploy, so a client that knows about this
     *  field can be talking to a server that does not yet send it, and the
     *  other way round for the minute a rollout takes. Anything reading it
     *  has to survive the field being absent, and a required type would
     *  have let that go unwritten -- it already did once, as a blank screen
     *  wherever the field was missing. */
    homeSite?: {
        code: string;
        name: string;
        /** One line, already punctuated, from the site's own columns.
         *
         *  OPTIONAL FOR THE SAME REASON AS THE FIELD AROUND IT: a bundle
         *  that knows about the address can be talking to a server from
         *  before it existed, and the counter's NAME is still worth
         *  showing on its own. Anything reading this renders nothing when
         *  it is absent rather than an empty line under the name.
         *
         *  Named as the sites endpoint names it, and punctuated the same
         *  way: one address, one spelling of it. */
        fullAddress?: string;
    } | null;
}

export interface DriverPick {
    /** TVHS's two vans. Null for every courier on a project without routes,
     *  which is what decides how they sign in: see Login.tsx. */
    route: string | null;
    username: string;
    name: string;
    /** Whether a route PIN is already set. Meaningless without a route. */
    hasPin: boolean;
}

export interface UserSummary {
    id: number;
    username: string;
    name: string;
    email: string | null;
    role: 'admin' | 'staff' | 'driver';
    status: 'active' | 'disabled';
    hasPin: boolean;
    created_at: string | null;
    memberships: Array<{ project_id: number; code: string; project_name: string; role: ProjectMembership['role']; settings: Record<string, unknown> }>;
}

/** An enrolled phone (ticket 2.3), as distinct from a live session. */
export interface EnrolledDevice {
    id: string;
    label: string;
    userAgent: string;
    createdAt: string;
    lastSeenAt: string;
    revokedAt: string | null;
    current: boolean;
}

/** What GET /api/login/device says about the phone the app is open on. */
export type DeviceIdentity =
    | { enrolled: false }
    | { enrolled: true; name: string; username: string; hasPin: boolean; label: string };

/** A disagreement between the system and reality (ticket 5.2). */
export interface Discrepancy {
    id: number;
    serviceDate: string;
    kind: string;
    severity: 'critical' | 'major' | 'minor';
    orderId: number | null;
    reference: string | null;
    expected: string;
    actual: string;
    reportedBy: string;
    reportedAt: string;
    status: 'open' | 'resolved' | 'accepted';
    resolution: string;
    resolvedBy: string;
    resolvedAt: string | null;
}

export interface DiscrepancySummary {
    days: Array<{ serviceDate: string; open: number; resolved: number; accepted: number; critical: number; major: number; minor: number }>;
    totals: { open: number; resolved: number; accepted: number };
    goLive: { openCritical: number; openTotal: number; ready: boolean; why: string };
}

export interface SessionSummary {
    id: string;
    device: string;
    /* Absent unless an administrator is reading. The server keeps the address
     * for the access trail and does not hand it to the account it belongs to:
     * see the note on /api/me/sessions. Optional rather than always-a-string
     * so a screen cannot render "undefined" in a column by forgetting. */
    ip?: string;
    created_at: string;
    last_seen_at: string;
    idle_expires_at: string;
    absolute_expires_at: string;
    current: boolean;
}

export interface AuditEvent {
    id: number;
    at: string;
    project_id: number | null;
    user_id: number | null;
    username: string | null;
    action: string;
    entity: string;
    entity_id: string | null;
    ip: string;
    detail: Record<string, unknown>;
}

/* Account and security screens: the audit log, the device list, a user's
 * sessions. Deliberately the reader's own zone, not a project's, because
 * these answer a personal question ("when did somebody sign in as me", "when
 * did I last use that phone") and two of the three screens are not scoped to
 * a project at all.
 *
 * Anything measured against the contract uses lib/when.ts instead, which
 * formats in the project's zone. Mixing those two up is the bug that module
 * exists to prevent, so the split is on purpose and worth leaving a note on. */
export const fmtWhen = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};
